// src/features/auth/context/AuthContext.js
import React, {
  createContext, useState, useContext,
  useEffect, useCallback,
} from 'react';
import api from '../../../api/axiosInstance';

const AuthContext = createContext(null);

const LEGACY_ROLE_MAP = {
  photographer: 'employee', editor: 'employee',
  project_manager: 'dept_manager', accountant: 'employee',
  custom: 'employee', admin: 'admin', dept_manager: 'dept_manager',
  employee: 'employee', client: 'client',
};

function normalizeUser(userData) {
  if (!userData) return null;
  const rawKey   = userData.role_key || '';
  const role_key = LEGACY_ROLE_MAP[rawKey] || rawKey || 'employee';
  return { ...userData, role_key,
    is_admin: role_key === 'admin', is_dept_manager: role_key === 'dept_manager',
    is_employee: role_key === 'employee', is_client: role_key === 'client',
  };
}

const KEYS = {
  access: 'access_token', refresh: 'refresh_token',
  authType: 'auth_type', clientToken: 'client_token',
};

const SECTION_ACCESS = {
  bookings: ['admin', 'dept_manager'], tasks: ['admin', 'dept_manager', 'employee'],
  treasury: ['admin'], reports: ['admin'], expenses: ['admin'],
  expense_categories: ['admin'], client_report: ['admin'],
  clients: ['admin', 'dept_manager'], users: ['admin'],
  activity_log: ['admin'],
  settings: ['admin'], client_portal: ['client'],
};

const OPERATION_ACCESS = {
  add_booking: ['admin', 'dept_manager'], change_booking: ['admin', 'dept_manager'],
  delete_booking: ['admin'], change_booking_status: ['admin', 'dept_manager'],
  add_task: ['admin', 'dept_manager'], change_task: ['admin', 'dept_manager'],
  change_own_task_status: ['employee'], delete_task: ['admin', 'dept_manager'],
  manage_deliverables: ['admin', 'dept_manager'],
  add_payment: ['admin', 'dept_manager'], delete_payment: ['admin'],
  add_expense: ['admin', 'dept_manager'], delete_expense: ['admin'],
  add_client: ['admin', 'dept_manager'], change_client: ['admin', 'dept_manager'],
  view_clients: ['admin', 'dept_manager'], add_user: ['admin'],
  change_user: ['admin'], view_users: ['admin'],
  view_financial_reports: ['admin'], view_client_reports: ['admin'],
  view_expenses: ['admin'], manage_expense_categories: ['admin'],
  manage_settings: ['admin'], manage_treasury: ['admin'],
  view_all_bookings: ['admin', 'dept_manager'], view_all_tasks: ['admin', 'dept_manager'],
  view_own_tasks: ['employee'],
};

export const AuthProvider = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loading, setLoading]           = useState(true);

  const checkLoggedInStatus = useCallback(async () => {
    const authType = localStorage.getItem(KEYS.authType);

    if (authType === 'client') {
      const token = localStorage.getItem(KEYS.clientToken);
      if (!token) { setLoggedInUser(null); setLoading(false); return; }
      try {
        const res = await api.get('/client/me/');
        setLoggedInUser(normalizeUser({
          id: res.data.id, username: res.data.phone,
          first_name: res.data.name, role_key: 'client',
          role: 'زبون', client: res.data,
        }));
      } catch {
        localStorage.removeItem(KEYS.clientToken);
        localStorage.removeItem(KEYS.authType);
        setLoggedInUser(null);
      } finally { setLoading(false); }
      return;
    }

    const token = localStorage.getItem(KEYS.access);
    if (!token) { setLoggedInUser(null); setLoading(false); return; }
    try {
      const res = await api.get('/me/');
      setLoggedInUser(normalizeUser(res.data));
    } catch {
      localStorage.removeItem(KEYS.access);
      localStorage.removeItem(KEYS.refresh);
      setLoggedInUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { checkLoggedInStatus(); }, [checkLoggedInStatus]);

  const login = useCallback((userData, tokens) => {
    localStorage.setItem(KEYS.access,   tokens.access);
    localStorage.setItem(KEYS.refresh,  tokens.refresh);
    localStorage.setItem(KEYS.authType, 'staff');
    setLoggedInUser(normalizeUser(userData));
  }, []);

  const clientLogin = useCallback((clientData, token) => {
    localStorage.setItem(KEYS.access,      token);
    localStorage.setItem(KEYS.clientToken, token);
    localStorage.setItem(KEYS.authType,    'client');
    setLoggedInUser(normalizeUser({
      id: clientData.id, username: clientData.phone,
      first_name: clientData.name, role_key: 'client',
      role: 'زبون', client: clientData,
    }));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEYS.access);
    localStorage.removeItem(KEYS.refresh);
    localStorage.removeItem(KEYS.clientToken);
    localStorage.removeItem(KEYS.authType);
    setLoggedInUser(null);
  }, []);

  // ✅ إضافة هذا الجزء للاستماع لحدث انتهاء الجلسة من Axios
  useEffect(() => {
    const handleUnauthorized = () => {
      // إشعار المستخدم (يمكنك إزالته إذا أردت خروجاً صامتاً تماماً)
      alert('انتهت صلاحية الجلسة. يرجى تسجيل الدخول مجدداً.');
      logout(); // استدعاء تسجيل الخروج بهدوء
    };

    window.addEventListener('unauthorized_access', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized_access', handleUnauthorized);
    };
  }, [logout]);

  const hasRole = useCallback((role) => {
    if (!loggedInUser) return false;
    return loggedInUser.role_key === role;
  }, [loggedInUser]);

  const canAccess = useCallback((section) => {
    if (!loggedInUser) return false;
    const allowed = SECTION_ACCESS[section];
    if (!allowed) return false;
    return allowed.includes(loggedInUser.role_key);
  }, [loggedInUser]);

  const hasPermission = useCallback((codename) => {
    if (!loggedInUser) return false;
    if (loggedInUser.is_admin) return true;
    const allowed = OPERATION_ACCESS[codename];
    if (!allowed) return false;
    return allowed.includes(loggedInUser.role_key);
  }, [loggedInUser]);

  return (
    <AuthContext.Provider value={{
      loggedInUser, loading, login, clientLogin, logout,
      hasRole, canAccess, hasPermission,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);